/* eslint-disable import/no-default-export, import/order, max-lines, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import { MIN_DIMENSION_SIZE } from "../common/constants";

export const getRangeString = (size: { category: string; sizeRange?: string }) =>
  `${size.category} ${size.sizeRange ? size.sizeRange : ""}`;

export const isWidthAndHeightValid = ({
  width,
  height
}: {
  width: number | string;
  height: number | string;
}) =>
  width !== "" &&
  Number(width) >= MIN_DIMENSION_SIZE &&
  height !== "" &&
  Number(height) >= MIN_DIMENSION_SIZE;

type PresetItem = {
  preset: string;
  quantity: number;
  unitPrice?: string;
  [key: string]: unknown;
};

type GroupedPreset = {
  items: Array<PresetItem & { index: number }>;
  count: number;
  price: number;
  preset: string;
};

export const groupByPreset = (sizesAndQuantities: PresetItem[]): Record<string, GroupedPreset> =>
  sizesAndQuantities.reduce(
    (acc: Record<string, GroupedPreset>, item: PresetItem, index: number) => {
      const { preset } = item;
      const quantity = Number(item.quantity) || 0;
      return {
        ...acc,
        [preset]: {
          items: [...(acc[preset]?.items || []), { ...item, index }],
          count: (acc[preset]?.count || 0) + quantity,
          price: item.unitPrice
            ? (acc[preset]?.price || 0) + Number(item.unitPrice) * quantity
            : acc[preset]?.price || 0,
          preset
        }
      };
    },
    {}
  );

export const getImageMetadataInfo = ({
  metadata,
  sizesAndQuantities,
  minDpi,
  artwork
}: {
  metadata: {
    aspectRatio: number;
    width: number;
    height: number;
    maxWidth: number;
    maxHeight: number;
    maxSheetWidth: number;
    maxSheetHeight: number;
  } | null;
  sizesAndQuantities: unknown[];
  minDpi: number;
  artwork: { name: string; fileSize: number; fileType: string };
}) => {
  if (!metadata) return null;

  return {
    aspect_ratio: metadata.aspectRatio,
    resolution_width: metadata.width,
    resolution_height: metadata.height,
    size_and_quantities: sizesAndQuantities,
    max_width: metadata.maxWidth,
    max_height: metadata.maxHeight,
    max_sheet_width: metadata.maxSheetWidth,
    max_sheet_height: metadata.maxSheetHeight,
    min_dpi: minDpi,
    file_name: artwork.name,
    file_size: artwork.fileSize,
    file_type: artwork.fileType
  };
};

// MN-7839: coerce to a finite positive number, or null. Accepts strings
// because the canonical ArtworkMetadata interface (types.ts:316) types
// `aspectRatio` as a string, and natural dims can arrive as strings after
// state round-trips through string-formatted inputs.
const toUsableRatio = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const ratio = typeof value === "string" ? Number(value) : value;
  return typeof ratio === "number" && Number.isFinite(ratio) && ratio > 0 ? ratio : null;
};

// Derive aspect ratio from artwork's natural dims when metadata's
// aspectRatio is null / 0 / undefined / NaN / Infinity / negative / non-numeric
// string. Returns null only when both metadata and natural dims are unusable —
// callers must then skip the lock (leave partner edge unchanged) instead of
// silently substituting an unrelated value.
const resolveAspectRatio = ({
  metadataAspectRatio,
  naturalWidth,
  naturalHeight
}: {
  metadataAspectRatio: number | string | null | undefined;
  naturalWidth: number | string | null | undefined;
  naturalHeight: number | string | null | undefined;
}): number | null => {
  const metadataRatio = toUsableRatio(metadataAspectRatio);
  if (metadataRatio !== null) return metadataRatio;

  const width = toUsableRatio(naturalWidth);
  const height = toUsableRatio(naturalHeight);
  if (width !== null && height !== null) {
    return toUsableRatio(width / height);
  }

  return null;
};

export const getProportionalEdge = ({
  value,
  field,
  metadataAspectRatio,
  naturalWidth,
  naturalHeight
}: {
  value: number;
  field: "width" | "height";
  metadataAspectRatio: number | string | null | undefined;
  naturalWidth: number | string | null | undefined;
  naturalHeight: number | string | null | undefined;
}): string | null => {
  const aspectRatio = resolveAspectRatio({ metadataAspectRatio, naturalWidth, naturalHeight });
  if (aspectRatio === null) return null;

  const factor = field === "height" ? 1 / aspectRatio : aspectRatio;
  const otherEdge = Math.round(value * factor * 100) / 100;

  return otherEdge.toFixed(2);
};
